import { useCallback, useContext, useMemo, useState } from 'react'
import { useDebounceCallback } from 'usehooks-ts'

import { type SanityFrameBuilderPassepartoutColor } from '@frame-builder/data/sanity/queries/types/passepartout'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import {
  getMaximumPassepartoutSize,
  getMinimumPassepartoutSize,
} from '@frame-builder/lib/default-state'
import { getItemTitle, toRecord } from '@frame-builder/lib/helpers'
import { type ItemPropsWithId } from '@frame-builder/lib/item'
import {
  getStatePrice,
  getStatePriceWithDiscount,
} from '@frame-builder/lib/state-price'
import { type FrameBuilderState } from '@frame-builder/lib/types'
import { generateSanityKey } from '@lib/nanoid'

import ItemImage from '@frame-builder/components/utility/item-image'
import ItemList from '@frame-builder/components/utility/item-list'
import PriceBadge from '@frame-builder/components/utility/price-badge'
import PassepartoutColorDetails from './passepartout-color-details'
import PassepartoutSizeControls from './passepartout-size-controls'

const noPassepartoutItemId = 'no-passepartout'

const PassepartoutColorList = () => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { frameBuilderConfiguration, frameBuilderSettings, frameBuilderType } =
    useContext(FrameBuilderSettingsContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { lastValidState, enabledItemMap } = useContext(
    FrameBuilderValidationContext,
  )

  const minimumPassepartoutSize =
    getMinimumPassepartoutSize(frameBuilderSettings)
  const maximumPassepartoutSize =
    getMaximumPassepartoutSize(frameBuilderSettings)

  const [passepartoutWidth, setPassepartoutWidth] = useState(
    Math.min(
      Math.max(selectedState.passepartout.width ?? 0, minimumPassepartoutSize),
      maximumPassepartoutSize,
    ),
  )
  const [passepartoutHeight, setPassepartoutHeight] = useState(
    Math.min(
      Math.max(selectedState.passepartout.height ?? 0, minimumPassepartoutSize),
      maximumPassepartoutSize,
    ),
  )

  // Calculate frame prices for comparison
  const noOptionFramePriceWithDiscount = useMemo(() => {
    const newState: FrameBuilderState = {
      ...committedState,
      passepartout: {
        ...committedState.passepartout,
        color: undefined,
      },
    }

    return getStatePriceWithDiscount(
      frameBuilderType,
      frameBuilderConfiguration,
      newState,
    )
  }, [committedState, frameBuilderConfiguration, frameBuilderType])

  const framePrices = useMemo(() => {
    return toRecord<number>(
      frameBuilderConfiguration.passepartoutColors.map((passepartoutColor) => {
        const newState: FrameBuilderState = {
          ...committedState,
          passepartout: {
            ...committedState.passepartout,
            color: passepartoutColor,
          },
        }

        return {
          [passepartoutColor._id]: getStatePrice(
            frameBuilderType,
            frameBuilderConfiguration,
            newState,
          ),
        }
      }),
    )
  }, [committedState, frameBuilderConfiguration, frameBuilderType])

  const framePricesWithDiscount = useMemo(() => {
    return toRecord<number>(
      frameBuilderConfiguration.passepartoutColors.map((passepartoutColor) => {
        const newState: FrameBuilderState = {
          ...committedState,
          passepartout: {
            ...committedState.passepartout,
            color: passepartoutColor,
          },
        }

        return {
          [passepartoutColor._id]: getStatePriceWithDiscount(
            frameBuilderType,
            frameBuilderConfiguration,
            newState,
          ),
        }
      }),
    )
  }, [committedState, frameBuilderConfiguration, frameBuilderType])

  const handlePassepartoutWidthChange = useCallback(
    (newWidth: number) => {
      dispatchSelectedState({
        type: 'passepartout',
        width: newWidth,
      })
    },
    [dispatchSelectedState],
  )
  const debouncedHandlePassepartoutWidthChange = useDebounceCallback(
    handlePassepartoutWidthChange,
    600,
  )

  const handlePassepartoutHeightChange = useCallback(
    (newHeight: number) => {
      dispatchSelectedState({
        type: 'passepartout',
        height: newHeight,
      })
    },
    [dispatchSelectedState],
  )
  const debouncedHandlePassepartoutHeightChange = useDebounceCallback(
    handlePassepartoutHeightChange,
    600,
  )

  // Prepare option items
  const items = useMemo(() => {
    const getNoPassepartoutItem = () => {
      const title = frameBuilderStrings.passepartoutNoPassepartoutTitle

      let imageElement: JSX.Element | undefined
      let detailsElement: JSX.Element | undefined
      let badgeElement: JSX.Element | undefined

      if (frameBuilderSettings.noOptionImage) {
        imageElement = (
          <ItemImage
            image={{
              _key: generateSanityKey(),
              _type: 'figure',
              ...frameBuilderSettings.noOptionImage,
            }}
            alt={title}
          />
        )
      }

      if (frameBuilderStrings.passepartoutNoPassepartoutDescription) {
        detailsElement = (
          <PassepartoutColorDetails
            description={
              frameBuilderStrings.passepartoutNoPassepartoutDescription
            }
          />
        )
      }

      if (selectedState.passepartout.color) {
        badgeElement = (
          <PriceBadge
            price={
              noOptionFramePriceWithDiscount -
              framePricesWithDiscount[selectedState.passepartout.color._id]
            }
            showPlusSign
          />
        )
      }

      const item: ItemPropsWithId = {
        id: noPassepartoutItemId,
        title,
        imageElement,
        detailsElement,
        badgeElement,
      }

      return item
    }

    const getPassepartoutItem = (
      passepartoutColor: SanityFrameBuilderPassepartoutColor,
    ) => {
      const isEnabled = enabledItemMap.passepartoutColorIds.includes(
        passepartoutColor._id,
      )
      const title = getItemTitle(
        frameBuilderStrings,
        passepartoutColor.title ?? '',
        isEnabled ? passepartoutColor.discountPercent : 0,
      )

      let imageElement: JSX.Element | undefined
      const subtitleElement = <>{passepartoutColor.subtitle}</>
      const detailsElement = (
        <PassepartoutColorDetails description={passepartoutColor.description}>
          <PassepartoutSizeControls
            width={passepartoutWidth}
            height={passepartoutHeight}
            min={minimumPassepartoutSize}
            max={maximumPassepartoutSize}
            setWidth={setPassepartoutWidth}
            setHeight={setPassepartoutHeight}
            onWidthChange={debouncedHandlePassepartoutWidthChange}
            onHeightChange={debouncedHandlePassepartoutHeightChange}
          />
        </PassepartoutColorDetails>
      )
      let badgeElement: JSX.Element | undefined

      if (!passepartoutColor.thumbnailImage && passepartoutColor.color.hex) {
        imageElement = (
          <div
            className="flex-none w-20 h-20 border border-gray-400"
            style={{
              backgroundColor: passepartoutColor.color.hex,
            }}
          />
        )
      }

      if (passepartoutColor.thumbnailImage) {
        imageElement = (
          <ItemImage image={passepartoutColor.thumbnailImage} alt={title} />
        )
      }

      if (
        selectedState.passepartout.color?._id !== passepartoutColor._id &&
        isEnabled
      ) {
        const currentPriceWithDiscount = selectedState.passepartout.color
          ? framePricesWithDiscount[selectedState.passepartout.color._id]
          : noOptionFramePriceWithDiscount
        badgeElement = (
          <>
            <PriceBadge
              price={
                framePricesWithDiscount[passepartoutColor._id] -
                currentPriceWithDiscount
              }
              showPlusSign
            />
            {!!passepartoutColor.discountPercent && (
              <PriceBadge
                price={
                  framePricesWithDiscount[passepartoutColor._id] -
                  framePrices[passepartoutColor._id]
                }
                showPlusSign
                disabled
              />
            )}
          </>
        )
      }

      const item: ItemPropsWithId = {
        id: passepartoutColor._id,
        title,
        imageElement,
        subtitleElement,
        detailsElement,
        badgeElement,
        disabled: !isEnabled,
        error: lastValidState.passepartout.color?._id === passepartoutColor._id,
      }

      return item
    }

    const items: ItemPropsWithId[] = []

    // Add "no passepartout" option only if this isn't custom passepartout builder
    if (frameBuilderType !== 'passepartout') {
      items.push(getNoPassepartoutItem())
    }

    frameBuilderConfiguration.passepartoutColors.forEach(
      (passepartoutColor) => {
        items.push(getPassepartoutItem(passepartoutColor))
      },
    )

    return items
  }, [
    debouncedHandlePassepartoutHeightChange,
    debouncedHandlePassepartoutWidthChange,
    enabledItemMap,
    frameBuilderConfiguration,
    frameBuilderSettings,
    frameBuilderStrings,
    frameBuilderType,
    framePrices,
    framePricesWithDiscount,
    lastValidState.passepartout.color,
    maximumPassepartoutSize,
    minimumPassepartoutSize,
    noOptionFramePriceWithDiscount,
    passepartoutHeight,
    passepartoutWidth,
    selectedState.passepartout.color,
  ])

  const handlePassepartoutColorChange = useCallback(
    (newId: string) => {
      if (newId === noPassepartoutItemId) {
        dispatchSelectedState({
          type: 'passepartout',
          color: undefined,
        })
        return
      }

      const newPassepartoutColor =
        frameBuilderConfiguration.passepartoutColors.find(
          (passepartoutColor) => passepartoutColor._id === newId,
        )

      if (newPassepartoutColor) {
        dispatchSelectedState({
          type: 'passepartout',
          color: newPassepartoutColor,
        })
      }
    },
    [frameBuilderConfiguration, dispatchSelectedState],
  )

  return (
    <ItemList
      items={items}
      defaultValue={
        selectedState.passepartout.color?._id ?? noPassepartoutItemId
      }
      onChange={handlePassepartoutColorChange}
    />
  )
}

export default PassepartoutColorList
